/**
 * Public agreement signing page at /agreements/sign/:token.
 *
 * Works for anyone (no login required) — token in URL grants access.
 * Captures: typed signature + drawn-canvas signature, 4 acceptance checkboxes,
 * IP/UA on the server. Plus optional ACH authorization (payment-plan template),
 * ID verification (winner-agreement template), and AI plain-English summary.
 */
import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  CheckCircle2, Shield, FileText, Download, Eraser, ChevronRight, Lock,
  Sparkles, Banknote, IdCard, Upload, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import Seo from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ------------- markdown → JSX (simple) ------------- */
function MarkdownBody({ md }) {
  const lines = (md || "").split("\n");
  const out = [];
  let key = 0;
  for (const line of lines) {
    const s = line.trim();
    if (!s) { out.push(<div key={key++} className="h-2" />); continue; }
    if (s.startsWith("## ")) {
      out.push(<h3 key={key++} className="font-serif text-2xl text-[var(--gold)] mt-8 mb-3">{s.slice(3)}</h3>);
    } else if (s.startsWith("# ")) {
      out.push(<h2 key={key++} className="font-serif text-3xl mt-8 mb-4">{s.slice(2)}</h2>);
    } else {
      out.push(<p key={key++} className="text-black/80 leading-relaxed mb-3">{s}</p>);
    }
  }
  return <>{out}</>;
}

/* ------------- AI plain-english summary ------------- */
function AiSummaryCard({ token, initial }) {
  const [summary, setSummary] = useState(initial || "");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  async function fetchSummary() {
    setLoading(true); setErr(null);
    try {
      const r = await axios.get(`${API}/agreements/by-token/${token}/summary`);
      setSummary(r.data.summary || "");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Summary unavailable.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="bg-gradient-to-br from-[#C8A96B]/10 to-transparent border border-[#C8A96B]/30 rounded-2xl p-6 mt-6"
      data-testid="ai-summary-card">
      <div className="flex items-start gap-3">
        <Sparkles className="w-5 h-5 text-[var(--gold)] flex-shrink-0 mt-1" />
        <div className="flex-1 min-w-0">
          <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-1">AI Plain-English Summary</p>
          <p className="text-xs text-black/50 mb-3">A 3-bullet explainer. Doesn't replace the full agreement.</p>
          {summary ? (
            <div className="text-sm text-black/80 whitespace-pre-line leading-relaxed" data-testid="ai-summary-text">
              {summary}
            </div>
          ) : (
            <button onClick={fetchSummary} disabled={loading}
              className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-lg border border-[#C8A96B]/40 bg-white hover:bg-[#C8A96B]/10 disabled:opacity-50"
              data-testid="ai-summary-generate">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {loading ? "Summarizing…" : "Summarize this for me"}
            </button>
          )}
          {err && <p className="text-xs text-red-600 mt-2">{err}</p>}
        </div>
      </div>
    </div>
  );
}

/* ------------- ACH form ------------- */
function AchForm({ token, onAuthorized }) {
  const [f, setF] = useState({
    account_holder: "",
    routing_number: "",
    account_number: "",
    billing_address: "",
    amount: "",
    recurring: false,
    signature_typed: "",
    auth_debit: false,
    auth_authorized: false,
    auth_terms: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  function set(k, v) { setF((p) => ({ ...p, [k]: v })); }

  async function submit() {
    if (!/^\d{9}$/.test(f.routing_number)) return toast.error("Routing number must be 9 digits.");
    if (!/^\d{4,17}$/.test(f.account_number)) return toast.error("Account number must be 4-17 digits.");
    if (!Number(f.amount) || Number(f.amount) <= 0) return toast.error("Amount must be greater than $0.");
    if (!f.auth_debit || !f.auth_authorized || !f.auth_terms) return toast.error("All three authorizations required.");
    if (!f.signature_typed.trim() || !f.account_holder.trim() || !f.billing_address.trim()) {
      return toast.error("Please fill all required fields.");
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/agreements/by-token/${token}/ach`, f);
      setDone(r.data);
      toast.success("ACH authorization captured.");
      onAuthorized?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "ACH submission failed.");
    } finally { setSubmitting(false); }
  }

  if (done) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 mt-6" data-testid="ach-done">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-1" />
          <div>
            <p className="font-serif text-xl">Payment authorization received</p>
            <p className="text-sm text-black/60 mt-1">
              Auth ID <span className="font-mono">{done.auth_id}</span> · ending in
              <span className="font-mono"> ****{done.account_last4}</span>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-[var(--line)] rounded-2xl p-8 mt-6" data-testid="ach-form">
      <h2 className="font-serif text-2xl flex items-center gap-2 mb-2">
        <Banknote className="w-5 h-5 text-[var(--gold)]" /> ACH Payment Authorization
      </h2>
      <p className="text-sm text-black/60 mb-6">
        Authorize LockedInForever to debit the bank account below per the terms of this agreement.
      </p>

      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <Field label="Account holder name" value={f.account_holder} onChange={(v) => set("account_holder", v)} testid="ach-holder" />
        <Field label="Amount (USD)" value={f.amount} onChange={(v) => set("amount", v)} type="number" testid="ach-amount" />
        <Field label="Routing number (9 digits)" value={f.routing_number} onChange={(v) => set("routing_number", v.replace(/\D/g, "").slice(0, 9))} testid="ach-routing" />
        <Field label="Account number" value={f.account_number} onChange={(v) => set("account_number", v.replace(/\D/g, "").slice(0, 17))} testid="ach-account" />
      </div>
      <Field label="Billing address" value={f.billing_address} onChange={(v) => set("billing_address", v)} testid="ach-billing" />
      <label className="flex items-center gap-2 text-sm mt-3 cursor-pointer">
        <input type="checkbox" checked={f.recurring} onChange={(e) => set("recurring", e.target.checked)}
          className="accent-[var(--gold)]" data-testid="ach-recurring" />
        Recurring authorization (monthly until paid in full)
      </label>

      <div className="bg-black/5 rounded-xl p-5 mt-5">
        <p className="text-[11px] uppercase tracking-widest text-black/50 mb-3">Required ACH authorizations</p>
        {[
          ["auth_debit", "I authorize LockedInForever to debit the account above for the amount(s) shown."],
          ["auth_authorized", "I am the account holder or authorized signer for this account."],
          ["auth_terms", "I have read and accept NACHA / ACH Originator terms; I may revoke in writing."],
        ].map(([k, label]) => (
          <label key={k} className="flex items-start gap-3 text-sm py-1.5 cursor-pointer" data-testid={`ach-check-${k}`}>
            <input type="checkbox" checked={f[k]} onChange={(e) => set(k, e.target.checked)}
              className="mt-1 accent-[var(--gold)]" />
            <span>{label}</span>
          </label>
        ))}
      </div>

      <div className="mt-5">
        <label className="text-[11px] uppercase tracking-widest text-black/50">Typed signature</label>
        <input value={f.signature_typed} onChange={(e) => set("signature_typed", e.target.value)} placeholder="/s/ Account Holder"
          className="w-full mt-1 px-4 py-3 rounded-xl border border-[var(--line)] outline-none focus:border-[var(--gold)] font-script text-2xl"
          data-testid="ach-signature" />
      </div>

      <button onClick={submit} disabled={submitting} className="btn-gold mt-6" data-testid="ach-submit">
        <Lock className="w-4 h-4" /> {submitting ? "Submitting…" : "Authorize payment"}
      </button>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-widest text-black/50">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} type={type}
        className="w-full mt-1 px-4 py-3 rounded-xl border border-[var(--line)] outline-none focus:border-[var(--gold)]"
        data-testid={testid} />
    </div>
  );
}

/* ------------- ID verification ------------- */
function IdVerifyCard({ token, status, onChange }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  async function upload() {
    if (!file) return toast.error("Please choose an ID image first.");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/agreements/by-token/${token}/id-verify`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("ID submitted for review.");
      onChange?.(r.data.status);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally { setBusy(false); }
  }

  const verified = status === "verified";
  const submitted = status === "submitted";
  const rejected = status === "rejected";

  return (
    <div className="bg-white border border-[var(--line)] rounded-2xl p-8 mt-6" data-testid="id-verify-card">
      <h2 className="font-serif text-2xl flex items-center gap-2 mb-2">
        <IdCard className="w-5 h-5 text-[var(--gold)]" /> Identity Verification
      </h2>
      <p className="text-sm text-black/60 mb-6">
        Upload a photo of your government-issued ID (driver's license, passport, or state ID).
        Required to release contest winnings. Stored securely; admin-reviewed.
      </p>

      {verified && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-2" data-testid="id-status-verified">
          <CheckCircle2 className="w-5 h-5 text-emerald-600" />
          <span className="text-sm">Identity verified.</span>
        </div>
      )}
      {submitted && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm" data-testid="id-status-submitted">
          ID submitted. Pending admin review (1-2 business days).
        </div>
      )}
      {rejected && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm mb-3" data-testid="id-status-rejected">
          Previous ID was rejected. Please upload a clearer image.
        </div>
      )}

      {(!status || rejected) && (
        <>
          <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="block w-full text-sm mt-3 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#C8A96B]/15 file:text-[var(--gold)] hover:file:bg-[#C8A96B]/25"
            data-testid="id-file-input" />
          {file && <p className="text-xs text-black/50 mt-2">Selected: {file.name} ({Math.round(file.size / 1024)} KB)</p>}
          <button onClick={upload} disabled={busy || !file} className="btn-gold mt-4" data-testid="id-upload-submit">
            <Upload className="w-4 h-4" /> {busy ? "Uploading…" : "Submit ID"}
          </button>
        </>
      )}
    </div>
  );
}

/* ------------- signature canvas ------------- */
export function SignatureCanvas({ onChange }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasInk, setHasInk] = useState(false);

  function getPoint(e) {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches?.[0];
    const x = (t ? t.clientX : e.clientX) - rect.left;
    const y = (t ? t.clientY : e.clientY) - rect.top;
    return { x: x * (c.width / rect.width), y: y * (c.height / rect.height) };
  }

  function start(e) {
    e.preventDefault();
    setDrawing(true);
    const p = getPoint(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  }
  function move(e) {
    if (!drawing) return;
    e.preventDefault();
    const p = getPoint(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(p.x, p.y);
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#0f0d0a";
    ctx.stroke();
    if (!hasInk) setHasInk(true);
  }
  function end() {
    if (!drawing) return;
    setDrawing(false);
    onChange?.(canvasRef.current.toDataURL("image/png"));
  }
  function clear() {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setHasInk(false);
    onChange?.(null);
  }

  return (
    <div>
      <div className="border-2 border-[#C8A96B]/30 rounded-xl bg-white relative overflow-hidden" style={{ aspectRatio: "3 / 1" }}>
        <canvas
          ref={canvasRef}
          width={900} height={300}
          className="w-full h-full touch-none cursor-crosshair"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          data-testid="signature-canvas"
        />
        {!hasInk && (
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center text-black/30 italic">
            Sign with mouse or finger
          </div>
        )}
      </div>
      <button onClick={clear} className="text-xs text-[var(--gold)] mt-2 flex items-center gap-1" data-testid="signature-clear">
        <Eraser className="w-3 h-3" /> Clear
      </button>
    </div>
  );
}

/* ------------- paper signing (download, sign, upload) ------------- */
function PaperSign({ token, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  async function upload() {
    if (!file) return toast.error("Pick the signed file first.");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/agreements/by-token/${token}/upload-signed`, fd);
      toast.success("Signed copy received!");
      onDone(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed."); } finally { setBusy(false); }
  }
  return (
    <div className="bg-white border border-[var(--line)] rounded-2xl p-8 mt-4" data-testid="paper-sign">
      <h2 className="font-serif text-2xl mb-2">Print, sign & upload</h2>
      <ol className="text-sm text-black/70 space-y-1 mb-5 list-decimal pl-5"><li>Download the PDF and print it.</li><li>Sign and date the last page.</li><li>Upload a scan or a clear photo below.</li></ol>
      <a href={`${API}/agreements/by-token/${token}/blank-pdf`} target="_blank" rel="noopener noreferrer" className="btn-outline-gold inline-flex" data-testid="paper-download"><Download className="w-4 h-4" /> Download PDF to sign</a>
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp,image/heic" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" data-testid="paper-file" />
        <button onClick={upload} disabled={!file || busy} className={`btn-gold ${!file || busy ? "opacity-40 cursor-not-allowed" : ""}`} data-testid="paper-upload"><Lock className="w-4 h-4" /> {busy ? "Uploading…" : "Upload signed copy"}</button>
      </div>
    </div>
  );
}

/* ------------- main page ------------- */
export default function AgreementSignPage() {
  const { token } = useParams();
  const [agr, setAgr] = useState(null);
  const [err, setErr] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [idStatus, setIdStatus] = useState(null);
  const docRef = useRef(null);

  const [fullName, setFullName] = useState("");
  const [typed, setTyped] = useState("");
  const [drawn, setDrawn] = useState(null);
  const [method, setMethod] = useState("online");
  const [checks, setChecks] = useState({
    accept_read: false, accept_binding: false, accept_e_records: false, accept_e_comm: false,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/agreements/by-token/${token}`)
      .then((r) => {
        setAgr(r.data);
        setFullName(r.data.assigned_name || "");
        setIdStatus(r.data.id_verification_status || null);
      })
      .catch((e) => setErr(e?.response?.status === 404 ? "not_found" : "error"));
  }, [token]);

  function onScroll(e) {
    if (scrolled) return;
    const el = e.target;
    const pct = (el.scrollTop + el.clientHeight) / el.scrollHeight;
    if (pct > 0.92) {
      setScrolled(true);
      axios.post(`${API}/agreements/by-token/${token}/event`, {
        kind: "scrolled", progress: 1.0,
      }).catch(() => null);
    }
  }

  async function decline() {
    if (!window.confirm("Decline this agreement? This cannot be undone.")) return;
    await axios.post(`${API}/agreements/by-token/${token}/event`, { kind: "declined" });
    toast.info("You declined this agreement.");
    setAgr({ ...agr, status: "declined" });
  }

  async function sign() {
    if (!fullName.trim() || !typed.trim()) return toast.error("Full name and typed signature required.");
    if (!Object.values(checks).every(Boolean)) return toast.error("Please check all 4 acceptance boxes.");
    if (!scrolled) return toast.error("Please scroll through the full agreement first.");
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/agreements/by-token/${token}/sign`, {
        full_name: fullName.trim(),
        signature_typed: typed.trim(),
        signature_drawn_data_url: drawn,
        ...checks,
      });
      toast.success("Agreement signed!");
      setAgr({ ...agr, status: "completed", doc_hash: r.data.doc_hash });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sign failed.");
    } finally {
      setSubmitting(false);
    }
  }

  if (err === "not_found") {
    return (
      <div className="min-h-screen bg-[var(--bg)] flex items-center justify-center p-6">
        <div className="text-center">
          <h1 className="font-serif text-4xl">Link expired or invalid</h1>
          <Link to="/" className="btn-gold mt-6 inline-block">Back to Home</Link>
        </div>
      </div>
    );
  }
  if (!agr) return <div className="min-h-screen flex items-center justify-center text-sm text-black/40">Loading…</div>;

  const isCompleted = agr.status === "completed";
  const isDeclined = agr.status === "declined";
  const isVoided = agr.status === "voided";

  return (
    <div className="min-h-screen bg-[var(--bg)]" data-testid="agreement-sign-page">
      <Seo title={`Sign: ${agr.title}`} path={`/agreements/sign/${token}`} noindex />

      <div className="container-wide max-w-4xl mx-auto py-12 px-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)]">LockedInForever · Legal</p>
            <h1 className="font-serif font-light text-4xl mt-1" data-testid="agreement-title">{agr.title}</h1>
            <p className="text-xs text-black/50 mt-2">Version {agr.version} · Document {agr.agreement_id}</p>
          </div>
          {isCompleted && (
            <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-full text-sm">
              <CheckCircle2 className="w-4 h-4" /> Completed
            </span>
          )}
          {isDeclined && <span className="px-3 py-1.5 bg-red-100 text-red-700 rounded-full text-sm">Declined</span>}
          {isVoided && <span className="px-3 py-1.5 bg-black/10 rounded-full text-sm">Voided</span>}
        </div>

        {/* AI summary */}
        <AiSummaryCard token={token} initial={agr.ai_summary} />

        {/* Document body */}
        <div
          ref={docRef}
          onScroll={onScroll}
          className="bg-white border border-[var(--line)] rounded-2xl p-8 max-h-[60vh] overflow-y-auto shadow-lg mt-6"
          data-testid="agreement-body"
        >
          <MarkdownBody md={agr.body_markdown} />
          <p className="text-xs text-black/40 italic mt-8">— end of agreement —</p>
        </div>

        {!scrolled && !isCompleted && !isDeclined && (
          <p className="text-xs text-amber-700 mt-3 text-center" data-testid="scroll-hint">
            ↓ Please scroll through the full agreement to enable signing
          </p>
        )}
        {scrolled && !isCompleted && !isDeclined && (
          <p className="text-xs text-emerald-700 mt-3 text-center flex items-center justify-center gap-1">
            <CheckCircle2 className="w-3 h-3" /> You've reviewed the full agreement
          </p>
        )}

        {/* Sign block */}
        {!isCompleted && !isDeclined && !isVoided && agr.sign_options?.includes("pdf") && (
          <div className="mt-8" data-testid="sign-method-chooser">
            <p className="text-[11px] uppercase tracking-widest text-black/50 mb-2">Choose how you'd like to sign</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {[["online", "Sign online", "Type and draw your signature here. Takes a minute."], ["pdf", "Print & sign (PDF)", "Download, sign on paper, upload a scan or photo."]].map(([k, l, d]) => (
                <button key={k} onClick={() => setMethod(k)} className={`text-left rounded-2xl border p-4 transition-colors ${method === k ? "border-[var(--gold)] bg-[var(--gold)]/10" : "border-[var(--line)] bg-white"}`} data-testid={`sign-method-${k}`}>
                  <p className="font-semibold">{l}</p><p className="text-xs text-black/60 mt-1">{d}</p></button>))}
            </div>
          </div>
        )}
        {!isCompleted && !isDeclined && !isVoided && method === "pdf" && (
          <PaperSign token={token} onDone={(r) => setAgr({ ...agr, status: "completed", doc_hash: r.doc_hash, sign_method: "upload", signed_at: new Date().toISOString(), full_name: agr.assigned_name })} />
        )}
        {!isCompleted && !isDeclined && !isVoided && method === "online" && (
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-[var(--line)] rounded-2xl p-8 mt-8"
          >
            <h2 className="font-serif text-2xl flex items-center gap-2 mb-2">
              <Shield className="w-5 h-5 text-[var(--gold)]" /> Sign electronically
            </h2>
            <p className="text-sm text-black/60 mb-6">
              By signing, you agree this electronic signature has the same legal effect as a handwritten one.
            </p>

            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="text-[11px] uppercase tracking-widest text-black/50">Full legal name</label>
                <input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Jane Vendor"
                  className="w-full mt-1 px-4 py-3 rounded-xl border border-[var(--line)] outline-none focus:border-[var(--gold)]"
                  data-testid="sign-fullname" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest text-black/50">Typed signature</label>
                <input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="/s/ Jane Vendor"
                  className="w-full mt-1 px-4 py-3 rounded-xl border border-[var(--line)] outline-none focus:border-[var(--gold)] font-script text-2xl"
                  data-testid="sign-typed" />
              </div>
            </div>

            <div className="mb-6">
              <label className="text-[11px] uppercase tracking-widest text-black/50 mb-2 block">
                Drawn signature (optional but recommended)
              </label>
              <SignatureCanvas onChange={setDrawn} />
            </div>

            <div className="bg-black/5 rounded-xl p-5 mb-6">
              <p className="text-[11px] uppercase tracking-widest text-black/50 mb-3">Required acknowledgments</p>
              {[
                ["accept_read", "I have read and agree to this agreement."],
                ["accept_binding", "I understand this agreement is legally binding."],
                ["accept_e_records", "I consent to electronic signatures and records."],
                ["accept_e_comm", "I agree to receive electronic communications."],
              ].map(([k, label]) => (
                <label key={k} className="flex items-start gap-3 text-sm py-1.5 cursor-pointer"
                  data-testid={`sign-check-${k}`}>
                  <input type="checkbox" checked={!!checks[k]}
                    onChange={(e) => setChecks({ ...checks, [k]: e.target.checked })}
                    className="mt-1 accent-[var(--gold)]" />
                  <span>{label}</span>
                </label>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              {(() => {
                const allChecked = Object.values(checks).every(Boolean);
                const ready = scrolled && allChecked && fullName.trim() && typed.trim() && !submitting;
                return (
                  <button onClick={sign} disabled={!ready}
                    className={`btn-gold flex-1 ${!ready ? "opacity-40 cursor-not-allowed" : ""}`}
                    data-testid="sign-submit">
                    <Lock className="w-4 h-4" /> {submitting ? "Signing…" : "Sign agreement"}
                    <ChevronRight className="w-4 h-4" />
                  </button>
                );
              })()}
              <button onClick={decline} className="text-sm px-4 py-3 text-red-600 hover:bg-red-50 rounded-xl border border-red-200"
                data-testid="sign-decline">Decline</button>
            </div>
          </motion.div>
        )}

        {/* Completed state */}
        {isCompleted && (
          <div className="bg-white border-2 border-[#C8A96B]/40 rounded-2xl p-8 mt-8 text-center" data-testid="signed-confirmation">
            <CheckCircle2 className="w-12 h-12 text-[var(--gold)] mx-auto mb-3" />
            <h2 className="font-serif text-3xl">Signed</h2>
            <p className="text-black/60 mt-2">{agr.full_name} · {agr.signed_at?.slice(0, 19).replace("T", " ")} UTC</p>
            <p className="text-[10px] font-mono text-black/40 mt-3 break-all">SHA-256: {agr.doc_hash}</p>
            <a href={`${API}/agreements/by-token/${token}/pdf`} target="_blank" rel="noopener"
              className="btn-gold mt-6 inline-block" data-testid="download-pdf">
              <Download className="w-4 h-4" /> Download PDF
            </a>
          </div>
        )}

        {/* Post-sign add-ons: ACH and ID verification */}
        {isCompleted && agr.requires_ach && (
          <AchForm token={token} />
        )}
        {isCompleted && agr.requires_id && (
          <IdVerifyCard token={token} status={idStatus} onChange={setIdStatus} />
        )}

        <p className="text-center text-xs text-black/40 mt-10">
          <FileText className="w-3 h-3 inline mr-1" />
          ESIGN Act-compliant. IP, device, and timestamps are recorded.
        </p>
      </div>
    </div>
  );
}
